import { useWatch } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  childErrorHospi,
  childrenGuaranteeIsRequired,
  isFraisGenRequired,
  isGarIncapRequired
} from '@@viewHelpers/validations/guaranteesPrevTns.js';
import { addIndex, compose, defaultTo, filter, forEach, isEmpty, map, propEq, propOr } from 'ramda';
import { isNilOrEmpty } from '@@serverHelpers';
import buildROIntervalsWithTabsAndFranchise from '../helpers/buildROIntervalsWithTabsAndFranchise.js';
import { getReimbursementGraphContent } from './helpers/getReimbursementGraphContent.js';
import { calculateHealthInsurancesWithFranchise } from './helpers/applyFranchiseToIntervals.js';
import { calculateDailyBaseAmount, EMPTY_RO_INTERVALS } from './helpers/intervalsHelpers.js';
import { enrichRODataWithTabs } from './helpers/enrichRODataWithTabs.js';
import { buildIncapFranchiseData } from './helpers/buildIncapFranchiseData.js';

const forEachIndexed = addIndex(forEach);

const useNiveauxGarantiesTarifsPrevTnsIncapaciteTemporaireDeTravailForm = ({
  form,
  guarantees,
  pricesGuarantees,
  getMiddleText,
  getMiddleBoolean,
  annualIncome,
  PASS
}) => {

  const lastValidValuesRef = useRef({
    healthInsurances: [],
    roIntervalsData: EMPTY_RO_INTERVALS
  });

  const garIncap = form.watch('guarantees.OPTION_INCAP.selected');
  const garOptionZeroSinistre = form.watch('guarantees.OPTION_ZERO_SIN.selected');
  const garOptionExoneration = form.watch('guarantees.OPTION_EXO_COT.selected');
  const garOptionConfortPsy = form.watch('guarantees.OPTION_CONFORT_PSY.selected');
  const incapHospiChild = form.watch('guarantees.OPTION_HOSP_ENF_GAR.selected');
  const optionFraisGeneraux = form.watch('guarantees.OPTION_FR_GEN.selected');
  const incapCapValue = form.watch('guarantees.OPTION_INCAP_CAP.value');

  // Observer les franchises choisies avec leur statut selected
  const franchiseToutesCauses = form.watch('guarantees.OPTION_FR_ALL.value');
  const franchiseMaladie = form.watch('guarantees.OPTION_FR_MAL.value');
  const franchiseAccident = form.watch('guarantees.OPTION_FR_ACC.value');
  const franchiseHospitalisation = form.watch('guarantees.OPTION_FR_HOSP.value');

  const watchedWarrantyFields = useWatch({
    control: form.control,
    name: filter(Boolean, [
      getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_MALADIE_EST_NECESSAIRE') &&
      'guarantees.OPTION_FR_MAL.value',
      getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_ACCIDENT_EST_NECESSAIRE') &&
      'guarantees.OPTION_FR_ACC.value',
      getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_HOSPITALISATION_EST_NECESSAIRE'
      ) && 'guarantees.OPTION_FR_HOSP.value',
      getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_TTES_CAUSES_EST_NECESSAIRE') &&
      'guarantees.OPTION_FR_ALL.value',
      'guarantees.OPTION_INCAP_CAP.value'
    ])
  });

  const watchedWarrantyFieldsKey = JSON.stringify(watchedWarrantyFields);

  useEffect(() => {
    form.trigger('guarantees.OPTION_INCAP.selected');
  }, [watchedWarrantyFieldsKey, form]);

  // reset les options si l'incap est désactivé
  const resetIncapSubOptionsWhenDisabled = useCallback(() => {
    if (form.getValues('guarantees.OPTION_INCAP.selected')) return;

    forEach(
      (code) => form.setValue(`guarantees.${code}.selected`, false),
      ['OPTION_ZERO_SIN', 'OPTION_EXO_COT', 'OPTION_CONFORT_PSY']
    );
  }, [form]);

  const {
    OPTION_INCAP,
    INCAP_MAD,
    OPTION_ZERO_SIN,
    OPTION_EXO_COT,
    OPTION_CONFORT_PSY,
    OPTION_HOSP_ENF_GAR,
    HOSP_ENF_MAD,
    OPTION_FR_GEN,
    OPTION_INCAP_CAP,
    HOSP_ENF_ALLOW_AMOUNT
  } = guarantees;

  const incapCotisation = [
    {
      displayed: garIncap,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_INCAP_CAP),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_INCAP_CAP)
    },
    {
      displayed: garOptionZeroSinistre,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_ZERO_SINISTRE_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_ZERO_SIN),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_ZERO_SIN)
    },
    {
      displayed: garOptionExoneration,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_EXO_COTISATION_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_EXO_COT),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_EXO_COT)
    },
    {
      displayed: garOptionConfortPsy,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_CONFORT_PSY_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_CONFORT_PSY),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_CONFORT_PSY)
    },
    {
      displayed: incapHospiChild,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', HOSP_ENF_ALLOW_AMOUNT),
      yearlyAmount: propOr(0.0, 'annualPrice', HOSP_ENF_ALLOW_AMOUNT)
    },
    {
      displayed: optionFraisGeneraux,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_FR_GEN),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_FR_GEN)
    }
  ];

  const updateRadioButtonSelectedIndex = (guaranteeCode) => (value) => {
    const options = form.getValues(`guarantees.${guaranteeCode}.options`);

    forEachIndexed((option, index) => {
      form.setValue(
        `guarantees.${guaranteeCode}.options.${index}.selected`,
        propEq('value', value, option)
      );
    })(options);
  };

  const getFranchise = () => {
    if (franchiseToutesCauses) return parseFloat(franchiseToutesCauses) || null;
    if (franchiseMaladie) return parseFloat(franchiseMaladie) || null;
    if (franchiseAccident) return parseFloat(franchiseAccident) || null;
    if (franchiseHospitalisation) return parseFloat(franchiseHospitalisation) || null;
    return null;
  };

  const franchise = getFranchise();

  const roIntervalsData = useMemo(() => {
    const result = garIncap
      ? buildROIntervalsWithTabsAndFranchise({
        guarantees,
        pricesGuarantees,
        getMiddleBoolean,
        configPrefix: 'NIVEAUXGARANTIESETTARIFS_GAR_INCAP',
        franchise
      })
      : EMPTY_RO_INTERVALS;

    const hasData = !isEmpty(propOr([], 'labels', result)) || !isEmpty(propOr([], 'ROs', result));
    if (hasData || !garIncap) {
      lastValidValuesRef.current.roIntervalsData = result;
      return result;
    }
    return lastValidValuesRef.current.roIntervalsData;
  }, [garIncap, guarantees, pricesGuarantees, getMiddleBoolean, franchise]);

  const healthInsurances = useMemo(() => {
    if (!garIncap || !annualIncome || !PASS || isNilOrEmpty(incapCapValue)) {
      return lastValidValuesRef.current.healthInsurances;
    }

    const percentage = defaultTo(0, parseFloat(incapCapValue));
    const baseAmount = calculateDailyBaseAmount(annualIncome, percentage);
    const intervals = propOr([], 'intervals', roIntervalsData);
    const ROs = propOr([], 'ROs', roIntervalsData);

    const result = calculateHealthInsurancesWithFranchise(
      intervals,
      ROs,
      baseAmount,
      annualIncome > PASS,
      franchise
    );

    if (!isEmpty(result)) {
      lastValidValuesRef.current.healthInsurances = result;
    }
    return result;
  }, [garIncap, annualIncome, PASS, incapCapValue, roIntervalsData, franchise]);

  const roIntervalsDataWithHealthInsurances = useMemo(
    () => enrichRODataWithTabs(roIntervalsData, {
      franchiseMap: {
        maladie: franchiseMaladie,
        accident: franchiseAccident,
        hospi: franchiseHospitalisation
      },
      buildIntervalsFunc: buildROIntervalsWithTabsAndFranchise,
      buildIntervalsParams: {
        guarantees,
        pricesGuarantees,
        getMiddleBoolean,
        configPrefix: 'NIVEAUXGARANTIESETTARIFS_GAR_INCAP'
      },
      annualIncome,
      PASS,
      capValue: incapCapValue
    }),
    [
      roIntervalsData,
      franchiseMaladie,
      franchiseAccident,
      franchiseHospitalisation,
      guarantees,
      pricesGuarantees,
      getMiddleBoolean,
      incapCapValue,
      annualIncome,
      PASS
    ]
  );

  const graphData = useMemo(() => {
    const labels = propOr([], 'labels', roIntervalsDataWithHealthInsurances);
    const ROs = propOr([], 'ROs', roIntervalsDataWithHealthInsurances);
    const tabsData = propOr([], 'tabs', roIntervalsDataWithHealthInsurances);

    const parsedHealthInsurances = map(compose(defaultTo(0), parseFloat), healthInsurances);
    const parsedROs = map(compose(defaultTo(0), parseFloat), ROs);
    const reimbursementGraphContent = getReimbursementGraphContent(healthInsurances, labels, ROs);

    return {
      labels,
      tabs: isEmpty(tabsData) ? undefined : tabsData,
      parsedHealthInsurances,
      parsedROs,
      graphRows: reimbursementGraphContent.rows,
      graphNote: reimbursementGraphContent.note
    };
  }, [roIntervalsDataWithHealthInsurances, healthInsurances]);

  const getComputedFranchiseData = useCallback(() => {
    const currentGarIncap = form.getValues('guarantees.OPTION_INCAP.selected');
    const currentIncapCapValue = form.getValues('guarantees.OPTION_INCAP_CAP.value');

    if (!currentGarIncap || isNilOrEmpty(currentIncapCapValue)) return {};

    const buildFranchiseData = (franchiseValue) => buildIncapFranchiseData({
      franchiseValue,
      guarantees,
      pricesGuarantees,
      getMiddleBoolean,
      incapCapValue: currentIncapCapValue,
      annualIncome,
      PASS
    });

    const hasToutesCausesConfig = getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_TTES_CAUSES_EST_NECESSAIRE');

    if (hasToutesCausesConfig) {
      const ttCauseValue = form.getValues('guarantees.OPTION_FR_ALL.value');
      if (ttCauseValue) {
        return { OPTION_FR_ALL: buildFranchiseData(parseFloat(ttCauseValue) || null) };
      }
    }

    const franchiseConfigs = [
      {
        code: 'OPTION_FR_MAL',
        configKey: 'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_MALADIE_EST_NECESSAIRE'
      },
      {
        code: 'OPTION_FR_ACC',
        configKey: 'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_ACCIDENT_EST_NECESSAIRE'
      },
      {
        code: 'OPTION_FR_HOSP',
        configKey: 'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_HOSPITALISATION_EST_NECESSAIRE'
      }
    ];

    return franchiseConfigs.reduce((result, {
      code,
      configKey
    }) => {
      const hasConfig = getMiddleBoolean(configKey);
      const value = form.getValues(`guarantees.${code}.value`);

      if (hasConfig && value) {
        result[code] = buildFranchiseData(parseFloat(value) || null);
      }
      return result;
    }, {});
  }, [form, guarantees, pricesGuarantees, getMiddleBoolean, annualIncome, PASS]);

  return {
    optionFraisGeneraux,
    watchedWarrantyFields,
    isFraisGenRequired,
    isGarIncapRequired,
    childrenGuaranteeIsRequired,
    childErrorHospi,
    garIncap,
    incapHospiChild,
    incapCotisation: filter(propEq('displayed', true), incapCotisation),
    OPTION_INCAP,
    INCAP_MAD,
    OPTION_ZERO_SIN,
    OPTION_EXO_COT,
    OPTION_CONFORT_PSY,
    OPTION_HOSP_ENF_GAR,
    HOSP_ENF_MAD,
    OPTION_FR_GEN,
    HOSP_ENF_ALLOW_AMOUNT,
    updateRadioButtonSelectedIndex,
    resetIncapSubOptionsWhenDisabled,
    roIntervalsData: roIntervalsDataWithHealthInsurances,
    healthInsurances,
    ...graphData,
    getComputedFranchiseData
  };
};

export default useNiveauxGarantiesTarifsPrevTnsIncapaciteTemporaireDeTravailForm;
